import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar'
import { Box, Loader, type PopoverProps } from '@mantine/core'
import { DateInput, type DateInputProps } from '@mantine/dates'
import { clsx } from 'clsx'
import { format } from 'date-fns'
import { useEffect, useState } from 'react'

import { i18n, useTranslation } from '@flecto/translations'

import { Icon } from '../Icon/Icon'
import styles from './styles.module.css'

export type DatePickerInputProps = {
  value?: string | null
  isLoading?: boolean
  onChange?: (date: string) => void
  onSelectDate?: (date: string) => void
  showCalendarIcon?: boolean
  popoverProps?: Omit<PopoverProps, 'children'>
} & Omit<DateInputProps, 'value' | 'onChange'>

export function DatePickerInput({
  value,
  onChange,
  isLoading,
  showCalendarIcon = true,
  onSelectDate,
  popoverProps = {
    withinPortal: true,
    zIndex: 9999,
  },
  ...props
}: DatePickerInputProps) {
  const [date, setDate] = useState<Date | null>(null)
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })

  const formatDate = (date: Date) =>
    `${format(date, 'yyyy-MM-dd')}T00:00:00`.replace('T00:00:00', '')

  const handleChangeDate = (date: Date | null) => {
    if (date) {
      date.setHours(0)
      date.setMinutes(0)
      date.setSeconds(0)
      date.setMilliseconds(0)
    }

    setDate(date)
    onChange?.(date ? formatDate(date) : '')
  }

  const handleDateParse = (input: string) => {
    const date = extractDateFromInput(input)

    onChange?.(formatDate(date))

    return date
  }

  const renderDay = (date: Date) => {
    const day = date.getDate()
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

    return (
      <Box>
        <Box
          onClick={() => onSelectDate?.(formatDate(date))}
          className={clsx(styles.datePickerDay, {
            [styles.datePickerDayToday]: date.getTime() === today.getTime(),
          })}
          data-testid={`date-time-picker-input-calendar-day-${day}`}
        >
          {day}
        </Box>
      </Box>
    )
  }

  useEffect(() => {
    if (!value) {
      setDate(null)
      return
    }

    const date = new Date(
      `${value.replace(/T(\d{2}:\d{2}:\d{2})(Z)?/, '')}T00:00:00`
    )

    if (date) {
      date.setHours(0)
      date.setMinutes(0)
      date.setSeconds(0)
      date.setMilliseconds(0)
    }

    setDate(date)
  }, [value])

  return (
    <DateInput
      placeholder={t('datetimepickerinput.placeholder_date')}
      {...props}
      value={date}
      leftSection={
        isLoading ? (
          <Loader size='xs' />
        ) : (
          showCalendarIcon && <Icon icon={faCalendar} />
        )
      }
      className={clsx(styles.datePickerInput, props.className)}
      dateParser={handleDateParse}
      locale={i18n.language}
      valueFormat='DD MMM YYYY'
      renderDay={renderDay}
      onChange={handleChangeDate}
      disabled={isLoading || props.disabled}
      popoverProps={popoverProps}
      clearable={props.clearable}
    />
  )
}

function extractDateFromInput(input: string) {
  const match = input.match(
    /(^0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/(\d{4}$)/
  )

  if (match) {
    const split = match[0].split('/').map((piece) => Number(piece))
    return new Date(split[2], split[1] - 1, split[0])
  }

  return new Date()
}
